import React, { useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import { useCases, useCasePrompts, heroStats } from '../data/mockData';
import { useLanguage } from '../context/LanguageContext';

const HeroSection = () => {
  const [selectedCase, setSelectedCase] = useState('aesthetic-clinic');
  const [displayText, setDisplayText] = useState('');
  const [cycleIndex, setCycleIndex] = useState(0);
  const { t } = useLanguage();

  const businessTypes = [
    { key: 'hero.businessTypes.aestheticClinic', color: '#3b82f6' },
    { key: 'hero.businessTypes.realEstate', color: '#2563eb' },
    { key: 'hero.businessTypes.beautySalon', color: '#1d4ed8' },
    { key: 'hero.businessTypes.dentalClinic', color: '#3b82f6' },
    { key: 'hero.businessTypes.medicalCenter', color: '#60a5fa' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCycleIndex((prev) => (prev + 1) % businessTypes.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [businessTypes.length]);

  useEffect(() => {
    setDisplayText(useCasePrompts[selectedCase]);
  }, [selectedCase]);

  return (
    <section className="pt-24 pb-16 bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-[#1a1a2e] leading-tight tracking-tight">
                {t('hero.title.line1')}
              </h1>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-[#1a1a2e] leading-tight tracking-tight">
                {t('hero.title.line2')}
              </h1>
              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight tracking-widest italic transition-colors duration-500"
                style={{ color: businessTypes[cycleIndex].color }}
              >
                {t(businessTypes[cycleIndex].key).toUpperCase()}
              </h1>
            </div>

            <p className="text-lg text-gray-600">
              {t('hero.subtitle')}{' '}
              <span className="font-semibold text-[#1a1a2e]">{t('hero.subtitleHighlight')}</span>{' '}
              {t('hero.subtitleEnd')}
            </p>

            {/* AI Prompt Input */}
            <div className="bg-[#1e293b] rounded-2xl p-6 shadow-2xl">
              <div className="relative">
                <p className="text-white text-base leading-relaxed pr-16 min-h-[80px]">
                  {displayText}
                </p>
                <button className="absolute bottom-0 right-0 w-12 h-12 bg-gradient-to-r from-[#3b82f6] to-[#2563eb] rounded-full flex items-center justify-center hover:scale-105 transition-transform">
                  <Send className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>

            {/* Use Case Tags */}
            <div className="space-y-3">
              <p className="text-sm text-gray-500">{t('hero.selectCase')}</p>
              <div className="flex flex-wrap gap-2">
                {useCases.map((useCase) => (
                  <button
                    key={useCase.id}
                    onClick={() => setSelectedCase(useCase.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedCase === useCase.id
                      ? 'bg-[#1e293b] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {useCase.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Content - Stats & Image */}
          <div className="relative">
            {/* Stats Cards */}
            <div className="absolute top-0 left-0 z-10 space-y-3">
              {heroStats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-[#1e293b]/90 backdrop-blur-sm rounded-xl p-4 max-w-[240px] transform hover:scale-105 transition-transform"
                >
                  <p className="text-2xl font-bold text-white">{stat.percentage}</p>
                  <p className="text-sm text-gray-300">{stat.description}</p>
                </div>
              ))}
            </div>

            {/* Hero Image */}
            <div className="ml-auto max-w-md lg:max-w-lg">
              <img
                src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=600&q=80"
                alt={t('hero.imageAlt')}
                className="rounded-2xl shadow-2xl w-full object-cover h-[500px]"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
